// The wind and water temperature box at the bottom of the map, for where the map is centred.
import type { Water } from "../../shared/model.ts";
import { $, $maybe, toast } from "../dom.ts";
import { fmtNum, t, tFmt, windFrom } from "../i18n.ts";
import { onRender } from "../state.ts";
import { fetchWaterTemp, waterSource } from "../water.ts";
import { view } from "../views.ts";
import { bigMap } from "./bigmap.ts";
import { onMapReady } from "./ready.ts";

interface Wind { at: L.LatLng; time: number; speed: number; dir: number; gust: number | null }
let wind: Wind | null = null, waterNow: (Water & { value: number }) | null = null, moveTimer = 0;
const OLD_MS = 10 * 6e4; // refresh after 10 minutes

async function updateWind(force: boolean): Promise<void> {
  if (!bigMap || !navigator.onLine) return;
  const c = bigMap.getCenter();
  if (!force && wind && c.distanceTo(wind.at) < 15000 && Date.now() - wind.time < 20 * 6e4) return;
  const getWind = (async () => {
    const q = new URLSearchParams({ latitude: c.lat.toFixed(3), longitude: c.lng.toFixed(3), current: "wind_speed_10m,wind_direction_10m,wind_gusts_10m", wind_speed_unit: "ms" });
    const cur = (await (await fetch("https://api.open-meteo.com/v1/forecast?" + q)).json()).current as
      { wind_speed_10m: number; wind_direction_10m: number | null; wind_gusts_10m: number | null } | undefined;
    if (cur && cur.wind_direction_10m != null) wind = { at: c, time: Date.now(), speed: cur.wind_speed_10m, dir: cur.wind_direction_10m, gust: cur.wind_gusts_10m };
  })().catch((e) => console.warn("Wind lookup failed", e));
  // same sources as for catches: nearest lake station, or the sea model at sea (water.ts)
  const getWater = fetchWaterTemp(c.lat, c.lng, Date.now())
    .then((w) => { waterNow = w.src !== "none" && w.value != null ? (w as Water & { value: number }) : null; })
    .catch((e) => console.warn("Water temperature lookup failed", e));
  await Promise.all([getWind, getWater]);
  drawWind();
}

function drawWind(): void {
  const b = $("#wind");
  if (!wind && !waterNow) { b.hidden = true; return; }
  b.hidden = false;
  const part = (sel: string) => $(sel, b);
  part(".wpart").hidden = !wind;
  if (wind) {
    part(".warrow").style.transform = `rotate(${wind.dir + 180}deg)`;
    part(".wspeed").textContent = fmtNum(wind.speed, 0) + " m/s";
    part(".wdir").textContent = windFrom(wind.dir) + (wind.gust != null ? " · " + t("gusts", fmtNum(wind.gust, 0)) : "");
  }
  part(".apart").hidden = !waterNow;
  if (waterNow) part(".wwater").textContent = fmtNum(waterNow.value, 0) + " °C";
  drawWindAge();
  const label = [wind ? `${t("windNow")}: ${part(".wspeed").textContent} ${part(".wdir").textContent}` : "",
    waterNow ? t("waterNowHint", fmtNum(waterNow.value, 1), waterSource(waterNow)) : ""].filter(Boolean).join(". ");
  b.setAttribute("aria-label", label);
}
// Where the wind comes from and how old it is, so you can trust it (or see that it's out of date).
function drawWindAge(): void {
  const m = $maybe("#wind .wmeta");
  if (!m) return;
  if (!wind) { m.textContent = ""; return; }
  const mins = Math.floor((Date.now() - wind.time) / 6e4);
  m.textContent = t("windSource", mins < 1 ? t("windJustNow") : t("windMinutes", mins));
  m.classList.toggle("stale", mins >= 30);
}
const mapOnScreen = () => bigMap != null && view === "map" && document.visibilityState === "visible";
setInterval(() => {
  if (!mapOnScreen()) return;
  if (wind && Date.now() - wind.time >= OLD_MS) void updateWind(true); else drawWindAge();
}, 60000);
document.addEventListener("visibilitychange", () => { if (mapOnScreen() && wind && Date.now() - wind.time >= OLD_MS) void updateWind(true); });
$("#wind").addEventListener("click", () => toast([
  wind ? t("windHint") + " " + t("windSourceLong", tFmt.format(wind.time)) : "",
  waterNow ? t("waterNowHint", fmtNum(waterNow.value, 1), waterSource(waterNow)) : "",
].filter(Boolean).join(" ")));

onMapReady((map) => {
  void updateWind(true);
  map.on("moveend", () => { clearTimeout(moveTimer); moveTimer = window.setTimeout(() => void updateWind(false), 800); });
});
onRender(drawWind);
